import { Alert, Button, Icon } from "@outschool/backpack";
import {
  ActivityEnrollQueryQuery,
  GetLearnerItems_EnrollmentFragment
} from "@outschool/gql-frontend-generated";
import { faBell } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { learnerSchedulePath, sectionWaitlistPath } from "@outschool/routes";
import { useWaitlistSeatClaimedMessageViewed } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { TabType, ViewType } from "../../routes/ParentSchedulePage";

interface SectionSoldOutProps {
  activity: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"];
  section: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  waitlistSeatsCount?: number;
  parentEnrollments?: GetLearnerItems_EnrollmentFragment[];
  parentLearners?: NonNullable<
    ActivityEnrollQueryQuery["currentUser"]
  >["children"];
  onClose: () => void;
}

export default function SectionSoldOut({
  activity,
  section,
  waitlistSeatsCount = 0,
  parentEnrollments = [],
  parentLearners = [],
  onClose
}: SectionSoldOutProps) {
  const { t } = useTranslation("client\\components\\payments\\SectionSoldOut");

  const Link = useLinkComponent();
  const { currentUser } = useSession();
  const trackWaitlistedNoAvailableSeats = useWaitlistSeatClaimedMessageViewed();
  const learnersEnrolled = parentEnrollments?.length;
  const allLearnersEnrolled =
    learnersEnrolled &&
    parentLearners.every(learner =>
      parentEnrollments?.find(
        enrollment => enrollment.learner?.uid === learner.uid
      )
    );

  const isOnWaitlist = !!(waitlistSeatsCount > 0);
  React.useEffect(() => {
    if (isOnWaitlist) {
      trackWaitlistedNoAvailableSeats({
        currentUserUid: currentUser?.uid,
        sectionUid: section.uid
      });
    }
  }, [currentUser, section, isOnWaitlist, trackWaitlistedNoAvailableSeats]);
  return (
    <>
      <Alert
        severity={allLearnersEnrolled ? "success" : "error"}
        title={
          allLearnersEnrolled ? t("You’re enrolled!") : t("No available seats")
        }
        sx={{ marginBottom: "2rem" }}
      >
        {t("All seats have already been claimed.")}{" "}
        {isOnWaitlist
          ? t("You are still on the waitlist for this class section.")
          : allLearnersEnrolled
          ? t("Your learner is already enrolled in this class section.", {
              count: learnersEnrolled,
              defaultValue_plural:
                "Your learners are already enrolled in this class section."
            })
          : null}
      </Alert>
      <Button
        variant="contained"
        onClick={onClose}
        sx={{ marginRight: "1rem" }}
      >
        {t("Done")}
      </Button>
      {allLearnersEnrolled ? (
        <Button variant="text" component={Link} to={learnerSchedulePath()}>
          {t("Manage Enrollments")}
        </Button>
      ) : isOnWaitlist ? (
        <Button
          variant="text"
          component={Link}
          to={learnerSchedulePath(ViewType.Classes, TabType.Waitlisted)}
        >
          {t("Manage Waitlists")}
        </Button>
      ) : (
        <Button
          variant="text"
          component={Link}
          to={sectionWaitlistPath(activity, section)}
          startIcon={<Icon icon={faBell} />}
        >
          {t("Join the waitlist")}
        </Button>
      )}
    </>
  );
}
